import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { forkJoin, map, noop, of, switchMap, tap, zip } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Store } from '@ngxs/store';
import { Pipeline, Project, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { Account, FieldDef } from 'app/core/models/account.types';
import { RelatedToType } from 'app/shared/enums';
import { PipelineService } from '../pipeline.service';
import { getAllowedFields } from 'app/shared/functions';
import { CustomField } from 'app/shared/modules/custom-form/custom-form.types';
import { FieldDefsState } from 'app/state/fields/state';
import { AccountState } from 'app/state/account/state';
import { Dictionary } from 'app/shared/models';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { DealsWithSamePropertiesComponent } from '../@shared/components/deals-with-same-properties/deals-with-same-properties.component';

declare const WebViewer: any;

declare interface CustomFormInterface {
  allowedFields: FieldDef[];
  commonData: Dictionary<any>;
  dataSources: Dictionary<any>;
  relatedToType: RelatedToType;
  renderAttachFileField: boolean;
  selectedFields: CustomField[];
  showFormTitle: boolean;
  showCancelButtons: { fromHeader: boolean; fromFooter: boolean };
  beforeSaveAction: (formValue: Dictionary<any>) => void;
  saveAction: (formValue: Dictionary<any>, extraData?: Dictionary<any>) => void;
  onTemplateChanged: (currentFields: CustomField[]) => void;
}

@Component({
  selector: 'project-pdf',
  templateUrl: './project-pdf.component.html',
  styleUrls: ['./project-pdf.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectPdfComponent extends OnDestroyMixin implements OnInit, AfterViewInit, OnDestroy {
  routeData: Data;
  pipeline: Pipeline;
  projectTemplates: ProjectTemplate<any>[];
  pdfUrl: string;
  document_id: string;
  selectedFields: CustomField[];
  customFormConfig: CustomFormInterface;

  private wvInstance: any;

  @ViewChild('viewer') viewer: ElementRef;

  get account(): Account {
    return this.store.selectSnapshot(AccountState.getAccount);
  }

  get fieldDefs(): FieldDef[] {
    return getAllowedFields(this.store.selectSnapshot(FieldDefsState.getFieldDefs), RelatedToType.Project);
  }

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _matDialog: MatDialog,
    private readonly store: Store,
    private readonly _pipelineService: PipelineService,
    private readonly _projectsService: ProjectsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.wvDocumentLoadedHandler = this.wvDocumentLoadedHandler.bind(this);

    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.routeData = data;
      this.pipeline = data.pipeline;

      this.setupSelectedFields();

      this.projectTemplates = this.routeData.options.project_templates
        .filter((option: ProjectTemplate<any>) => !option.hidden && option.related_to_type == RelatedToType.Project)
        .sort((a: ProjectTemplate<any>, b: ProjectTemplate<any>) => a.name.localeCompare(b.name));

      this.setupCustomFormConfig();
    });

    this._activatedRoute.queryParams.pipe(untilComponentDestroyed(this)).subscribe((params) => {
      this.pdfUrl = decodeURIComponent(params['pdf_url']);
      this.document_id = params['document_id'];
    });
  }

  ngAfterViewInit(): void {
    WebViewer(
      {
        path: '/assets/webviewer/public', // point to where the files you copied are served from
        initialDoc: this.pdfUrl, // path to your document
      },
      this.viewer.nativeElement,
    ).then((instance) => {
      this.wvInstance = instance;
      const { Tools } = instance;

      instance.disableTools([
        'AnnotationCreateTextHighlight',
        'AnnotationCreateTextUnderline',
        'AnnotationCreateTextSquiggly',
        'AnnotationCreateTextStrikeout',
      ]);
      instance.disableElement('downloadButton');
      instance.disableElement('printButton');
      instance.disableElement('freeHandToolGroupButton');
      instance.disableElement('textToolGroupButton');
      instance.disableElement('shapeToolGroupButton');
      instance.disableElement('signatureToolButton');
      instance.disableElement('freeTextToolButton');
      instance.disableElement('eraserToolButton');
      instance.disableElement('stickyToolButton');
      instance.disableElement('miscToolGroupButton');
      instance.disableElement('viewControlsButton');
      instance.disableElement('leftPanel');
      instance.disableElement('menuButton');
      instance.disableElement('pageNavOverlay');

      let myTool = {
        toolName: 'MyTool',
        toolObject: new Tools.LineCreateTool(),
        buttonImage: 'https://www.pdftron.com/favicon-32x32.png',
        buttonName: 'myToolButton',
        buttonGroup: 'textTools',
        tooltip: 'MyTooltip',
      };
      instance.registerTool(myTool);

      instance.docViewer.on('documentLoaded', this.wvDocumentLoadedHandler);
    });
  }

  private wvDocumentLoadedHandler(): void {
    const annotManager = this.wvInstance.annotManager;
    const { Annotations } = this.wvInstance;
    const rectangle = new Annotations.RectangleAnnotation();
    rectangle.PageNumber = 1;
    rectangle.X = 100;
    rectangle.Y = 100;
    rectangle.Width = 250;
    rectangle.Height = 250;
    rectangle.StrokeThickness = 5;
    rectangle.Author = annotManager.getCurrentUser();
    // see https://www.pdftron.com/api/web/WebViewer.html for the full list of low-level APIs
  }

  private setupSelectedFields(): CustomField[] {
    const fieldExcludeList = ['cff_address'];
    let initialFields = [];

    if (typeof this.pipeline.create_form_definition === 'string') {
      initialFields = JSON.parse(this.pipeline.create_form_definition);
    } else if (this.pipeline.create_form_definition?.fields) {
      initialFields = this.pipeline.create_form_definition.fields;
    }

    initialFields = initialFields.filter(({ name }) => !fieldExcludeList.includes(name));

    if (!initialFields.some((obj) => obj.name === 'cff_add_properties')) {
      initialFields.push({ name: 'cff_add_properties', isRequired: false, isRemovable: true });
    }

    return initialFields.length
      ? initialFields
      : [
          { name: 'title', isRequired: true, isRemovable: false },
          { name: 'property_type_id', isRequired: true, isRemovable: false },
          { name: 'template_id', isRequired: true, isRemovable: false },
          { name: 'cff_add_properties', isRequired: false, isRemovable: true },
        ];
  }

  private setupCustomFormConfig(): void {
    this.customFormConfig = {
      allowedFields: this.fieldDefs,
      commonData: { pipelineId: this.pipeline.id, formFieldData: this.routeData },
      dataSources: { template_id: this.projectTemplates },
      relatedToType: RelatedToType.Project,
      renderAttachFileField: false,
      selectedFields: this.setupSelectedFields(),
      showFormTitle: false,
      showCancelButtons: { fromHeader: false, fromFooter: false },
      saveAction: this.save.bind(this),
      beforeSaveAction: this.saveSingleOrMultipleAdresses.bind(this),
      onTemplateChanged: this.onTemplateChanged.bind(this),
    } as CustomFormInterface;
  }

  private saveSingleOrMultipleAdresses(formValue: Dictionary<any>) {
    const addresses = formValue.properties?.map((property: Property) => {
      return `${property.address['address'] || property.address['street']} ${property.address['city']}, ${
        property.address['state']
      } ${property.address['zip'] || property.address['postal_code']}`;
    }) as string[];

    let addressStreamHandler = of([]);

    if (addresses?.length) {
      addressStreamHandler = zip(
        forkJoin(
          addresses.map((address) =>
            this._projectsService
              .getProjectsNoPipeline(
                ['pipeline_id', 'project_stage', 'created_at', 'updated_at'],
                { location: { addressString: address, within: '0.1mi' } },
                0,
                20,
                'projects.created_at',
                'desc',
                '',
                true,
              )
              .pipe(
                map(
                  (response: { projects: Project[] }) =>
                    response?.projects.map((project) => ({ ...project, matchedBy: address })) as Project[],
                ),
              ),
          ),
        ),
      );
    }

    return addressStreamHandler.pipe(
      map((deals) => deals.reduce((p, c) => p.concat(...c), [] as Project[])),
      switchMap((similarDeals) => {
        if (similarDeals?.length) {
          return this._matDialog
            .open(DealsWithSamePropertiesComponent, { data: { similarDeals } })
            .afterClosed()
            .pipe(map((result) => result as boolean));
        }

        return of(true);
      }),
    );
  }

  private onTemplateChanged(fields: CustomField[]): void {
    this._pipelineService
      .update({ ...this.pipeline, create_form_definition: JSON.stringify({ fields }) }, Number(this.account.id))
      .pipe(untilComponentDestroyed(this))
      .subscribe();
  }

  private save(formValue: Dictionary<any>): void {
    const project = { ...formValue, pipeline_id: this.pipeline.id, document_id: this.document_id } as Project;

    this._projectsService
      .saveProject(project)
      .pipe(
        untilComponentDestroyed(this),
        switchMap((project: Project) => {
          const properties: Property[] = formValue.properties;

          if (properties?.length) {
            return forkJoin(
              properties.map((property) => {
                if (property.address?.id) {
                  return this._projectsService.relatePropertyToProject(property?.address as Property, project);
                } else {
                  return this._projectsService.addProperty(property, project);
                }
              }),
            ).pipe(
              switchMap((properties) => {
                project.properties = properties;
                return of(project);
              }),
            );
          }

          return of(project);
        }),
        tap((projectResult: Project) => {
          this._router.navigateByUrl(`/projects/${projectResult.pipeline_id}/${projectResult.id}`);
        }),
      )
      .subscribe(noop);
  }

  discard(): void {
    this._router.navigate(['../'], { relativeTo: this._activatedRoute });
  }
}
